import { r as createLogger } from "../logger-BU8YqMGU.js";
import { BlobReader, TextWriter, Uint8ArrayReader, ZipReader } from "@zip.js/zip.js";
//#region ../src/storage/zip-reader.ts
/**
* Zip Reader — utilities for reading recording session zip files.
*
* Provides production-ready functions to enumerate zip entries, extract
* recorded Redux actions, and load session metadata from exported recording
* zip files. Works with both Node.js (Uint8Array from fs) and browser
* (Uint8Array from fetch/FileReader) environments.
*
* The recording zip format:
*   actions/000001.json  — Redux action JSON files (1-based, zero-padded)
*   actions/000002.json
*   ...
*   images/frame-000001.jpg  — captured image frames (optional; legacy: frames/)
*   session.json             — session metadata (optional, see F1 bug)
*/
const log = createLogger("ZipReader");
/**
* Wrap a {@link ZipSource} in a fresh zip.js ZipReader.
*
* Exported deliberately: consumers outside this workspace resolve this
* module from the published dist at runtime and probe
* `typeof toZipReader === 'function'` as the capability marker for lazy
* Reader support before passing anything other than Uint8Array (older
* published versions accept only Uint8Array). Do not rename or remove
* without treating it as a breaking API change.
*/
function toZipReader(source) {
	return new ZipReader(source instanceof Uint8Array ? new Uint8ArrayReader(source) : source);
}
/**
* Maximum allowed uncompressed size (in bytes) for a single action or
* metadata JSON file inside a recording zip. Entries exceeding this limit
* are rejected before decompression to prevent out-of-memory conditions
* from malicious or corrupted zip files.
*
* 1 MB — generous for Redux action JSON; typical actions are a few KB.
*/
const MAX_ACTION_FILE_SIZE = 1048576;
/**
* Read all entries from a zip file.
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @returns Array of zip entries (directories and files)
*/
async function readZipEntries(data) {
	const reader = toZipReader(data);
	try {
		return await reader.getEntries();
	} finally {
		await reader.close();
	}
}
/**
* Extract the numeric index from an action filename.
* Filenames are zero-padded like "000001.json" or "actions/000001.json".
*
* @param filename - The entry filename from the zip
* @returns The numeric index, or NaN if the filename doesn't match the pattern
*/
function extractActionIndex(filename) {
	const match = /(\d+)\.json$/.exec(filename);
	return match ? parseInt(match[1], 10) : NaN;
}
/**
* Load all recorded Redux actions from a zip file.
*
* Filters for JSON files in the actions/ directory, parses them, and returns
* them sorted by their filename index (chronological order for replay).
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE)
* @returns Array of action entries sorted by index
*/
async function loadActionsFromZip(data, maxFileSize = MAX_ACTION_FILE_SIZE) {
	const actionEntries = (await readZipEntries(data)).filter((e) => !e.directory && e.filename.includes("actions/") && e.filename.endsWith(".json")).sort((a, b) => a.filename.localeCompare(b.filename));
	const results = [];
	for (const entry of actionEntries) if (!entry.directory) {
		if (entry.uncompressedSize > maxFileSize) throw new Error(`Action file "${entry.filename}" (${entry.uncompressedSize} bytes) exceeds maximum allowed size of ${maxFileSize} bytes`);
		const index = extractActionIndex(entry.filename);
		if (Number.isNaN(index)) log.warn(`Unexpected non-numeric action filename: "${entry.filename}". Expected pattern like "actions/000001.json". The file will still be processed.`);
		const text = await entry.getData(new TextWriter());
		let action;
		try {
			const parsed = JSON.parse(text);
			if (typeof parsed !== "object" || parsed === null || typeof parsed.type !== "string") {
				log.warn(`Skipping "${entry.filename}": parsed JSON is not a valid action (missing "type" string)`);
				continue;
			}
			action = parsed;
		} catch {
			log.warn(`Skipping malformed JSON in "${entry.filename}": parse failed`);
			continue;
		}
		results.push({
			index,
			filename: entry.filename,
			action
		});
	}
	return results;
}
/**
* Extract session metadata from an already-constructed ZipReader.
*
* Finds the `session.json` entry, validates its size, extracts and parses it.
* The reader is always closed in `finally`, so callers must not reuse it.
*
* @param reader - An open ZipReader (Uint8ArrayReader or BlobReader based)
* @param maxFileSize - Maximum allowed uncompressed size for session.json
* @returns Parsed metadata object, or null if session.json is absent
*/
async function extractSessionMetadataFromReader(reader, maxFileSize) {
	try {
		const sessionEntry = (await reader.getEntries()).find((e) => !e.directory && e.filename.endsWith("session.json"));
		if (!sessionEntry) return null;
		if (sessionEntry.uncompressedSize > maxFileSize) throw new Error(`Session file "${sessionEntry.filename}" (${sessionEntry.uncompressedSize} bytes) exceeds maximum allowed size of ${maxFileSize} bytes`);
		const text = await sessionEntry.getData(new TextWriter());
		return JSON.parse(text);
	} finally {
		await reader.close();
	}
}
/**
* Load session metadata from session.json in the zip file.
*
* Returns null if session.json is absent (graceful degradation for
* recordings created before the metadata writing bug was fixed).
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE)
* @returns Parsed session metadata, or null if not found
*/
async function loadSessionMetadata(data, maxFileSize = MAX_ACTION_FILE_SIZE) {
	return extractSessionMetadataFromReader(toZipReader(data), maxFileSize);
}
/**
* Load session metadata from session.json in a zip file provided as a Blob.
*
* Unlike `loadSessionMetadata(Uint8Array)`, this variant uses zip.js's
* BlobReader which reads only the central directory and the target entry
* from the Blob — not the entire file contents. This is critical for
* memory-efficient scanning of many large recording zips (e.g., during
* `discoverScenariosFromZipMetadata`).
*
* Since `File` extends `Blob`, this works directly with file handles:
*   const file = await handle.getFile();
*   const meta = await loadSessionMetadataFromBlob(file);
*
* @param blob - A Blob or File containing the zip data
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE)
* @returns Parsed session metadata, or null if session.json is not found
*/
async function loadSessionMetadataFromBlob(blob, maxFileSize = MAX_ACTION_FILE_SIZE) {
	return extractSessionMetadataFromReader(new ZipReader(new BlobReader(blob)), maxFileSize);
}
/**
* Extract GPS coordinates from a recording zip provided as a Blob.
*
* Uses BlobReader for memory-efficient reading. Reads all action JSON files,
* identifies `gpsData/recordGpsEvent` actions, and returns only the lightweight
* `{ lat, lng }` pairs — all other action data is discarded immediately.
*
* Returns coordinates in chronological order (sorted by action filename).
* Returns an empty array if the zip is invalid or contains no GPS actions.
*
* @param blob - A Blob or File containing the zip data
* @param maxFileSize - Maximum allowed uncompressed size per action entry
* @returns Array of GPS coordinates in chronological order
*/
async function loadGpsPathFromBlob(blob, maxFileSize = MAX_ACTION_FILE_SIZE) {
	const reader = new ZipReader(new BlobReader(blob));
	try {
		const actionEntries = (await reader.getEntries()).filter((e) => !e.directory && e.filename.includes("actions/") && e.filename.endsWith(".json")).sort((a, b) => a.filename.localeCompare(b.filename));
		const coords = [];
		for (const entry of actionEntries) {
			if (entry.uncompressedSize > maxFileSize) continue;
			const text = await entry.getData(new TextWriter());
			let action;
			try {
				action = JSON.parse(text);
			} catch {
				continue;
			}
			const gps = action.payload?.rawGpsPoint ?? action.payload?.gpsPoint;
			if (action.type === "gpsData/recordGpsEvent" && gps && typeof gps.latitude === "number" && typeof gps.longitude === "number") {
				const accuracy = typeof gps.latLongAccuracy === "number" && gps.latLongAccuracy > 0 ? gps.latLongAccuracy : void 0;
				coords.push({
					lat: gps.latitude,
					lng: gps.longitude,
					...accuracy !== void 0 ? { accuracy } : {}
				});
			}
		}
		return coords;
	} catch {
		return [];
	} finally {
		try {
			await reader.close();
		} catch {}
	}
}
/**
* Enumerate every file under a single top-level subdirectory of a recording
* zip. Mirrors the writer-side {@link ZipExportContributor} seam so consumers
* (typically the recorder) can read back what they wrote without
* re-implementing zip enumeration.
*
* Skips directory entries and files outside `subdir`. Sorts entries by
* `relativePath` for deterministic iteration. Returns an empty array when
* the subdir is absent (graceful degradation for older zips).
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param subdir - Top-level subdir to scan (no leading or trailing `/`)
*/
async function loadEntriesFromSubdir(data, subdir) {
	if (!subdir || subdir.includes("/") || subdir.startsWith(".")) throw new Error(`subdir must be a non-empty single path segment, got: ${JSON.stringify(subdir)}`);
	const prefix = `${subdir}/`;
	return (await readZipEntries(data)).filter((e) => !e.directory && e.filename.startsWith(prefix) && e.filename !== prefix).sort((a, b) => a.filename.localeCompare(b.filename)).map((entry) => ({
		relativePath: entry.filename.slice(prefix.length),
		fullPath: entry.filename,
		uncompressedSize: entry.uncompressedSize,
		getText: () => entry.getData(new TextWriter())
	}));
}
//#endregion
export { MAX_ACTION_FILE_SIZE, loadActionsFromZip, loadEntriesFromSubdir, loadGpsPathFromBlob, loadSessionMetadata, loadSessionMetadataFromBlob, readZipEntries, toZipReader };
